import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
    DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
    BookOpen, Plus, Sparkles, Loader2, Radio, Users, Award, Settings2, UserCog,
    Rocket, FileEdit, Trash2, RotateCcw,
} from "lucide-react";
import CreateProgramWizard from "@/components/wizard/CreateProgramWizard";
import VideoRenderJobsStrip from "@/components/curriculum/VideoRenderJobsStrip";

const CONTAINER = {
    hidden: {},
    show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
const ITEM = {
    hidden: { opacity: 0, y: 14 },
    show: {
        opacity: 1, y: 0,
        transition: { type: "spring", stiffness: 320, damping: 28 },
    },
};

/**
 * SMEDashboard — single-page Subject-Matter Expert console.
 *
 * SMEs are content authors who can:
 *   • create new programs from scratch (just like an admin)
 *   • assign each program to any instructor in the college
 *   • keep editing the chapters / lessons of programs they author
 *
 * They explicitly cannot touch students or other admin-only data.
 *
 * The page mounts a list of every non-archived program (so SME can audit
 * the whole catalogue), with two CTAs per row: "Reassign instructor" and
 * "Open editor". A 'New program' button at the top opens the same Create
 * dialog used elsewhere.
 */
export default function SMEDashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [programs, setPrograms] = useState([]);
    const [instructors, setInstructors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [reassignTarget, setReassignTarget] = useState(null); // {id,title,instructor_id}
    const [reassignTo, setReassignTo] = useState("");
    const [reassigning, setReassigning] = useState(false);
    const [publishingId, setPublishingId] = useState(null);
    // {id, title} when the user clicks the trash icon on a program row.
    // Set to null when the dialog is dismissed or the deletion completes.
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // Soft-deleted programs the current SME can restore themselves
    // (scoped to their own deletions; admins handle the global trash).
    const [myDeletions, setMyDeletions] = useState([]);
    const [restoringId, setRestoringId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [progRes, instRes, delRes] = await Promise.all([
                api.get("/programs?include_archived=true"),
                api.get("/admin/instructors"),
                // 4xx is non-fatal — the strip just stays hidden if this
                // endpoint isn't reachable (e.g. brand-new SME w/o
                // deletions, network blip, etc).
                api.get("/sme/my-deletions").catch(() => ({ data: [] })),
            ]);
            setPrograms(progRes.data || []);
            setInstructors(instRes.data || []);
            setMyDeletions(delRes.data || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const instructorById = useMemo(() => {
        const map = {};
        for (const i of instructors) map[i.id] = i;
        return map;
    }, [instructors]);

    // "My drafts" — programs *I* authored that aren't published yet.
    // We surface these in a dedicated strip so SMEs see what they still
    // owe the catalogue. Authored-by-someone-else drafts stay in the
    // main catalogue list below so a SME can still find them.
    const myDrafts = useMemo(() => {
        if (!user?.id) return [];
        return programs.filter(
            (p) =>
                p.created_by === user.id &&
                !p.is_published &&
                !p.is_archived,
        );
    }, [programs, user]);

    const publishProgram = async (p) => {
        setPublishingId(p.id);
        try {
            await api.patch(`/admin/programs/${p.id}`, { is_published: true });
            toast.success(`"${p.title}" is now live in the catalogue.`);
            await load();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not publish. Try again.",
            );
        } finally {
            setPublishingId(null);
        }
    };

    /**
     * Soft-delete the program (moves it to the admin's trash). The backend
     * `DELETE /admin/programs/{id}` accepts SMEs as of this iteration.
     * Programs in trash are hidden from the SME catalogue list, the public
     * site, and student dashboards. Admins can restore from `/admin/programs`.
     */
    const deleteProgram = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/programs/${deleteTarget.id}`);
            toast.success(
                `"${deleteTarget.title}" was deleted. An admin can restore it from the trash if needed.`,
            );
            setDeleteTarget(null);
            await load();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not delete. Try again.",
            );
        } finally {
            setDeleting(false);
        }
    };

    /**
     * Restore an SME-owned soft-deleted program. Backend enforces that
     * SMEs can only restore programs whose `deleted_by` matches their
     * own user id — admin deletions are out of scope for the SME view.
     */
    const restoreProgram = async (p) => {
        setRestoringId(p.id);
        try {
            await api.post(`/admin/programs/${p.id}/restore`);
            toast.success(`"${p.title}" was restored to your catalogue.`);
            await load();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not restore. Try again.",
            );
        } finally {
            setRestoringId(null);
        }
    };

    const submitReassign = async () => {
        if (!reassignTarget || !reassignTo) return;
        setReassigning(true);
        try {
            await api.patch(`/admin/programs/${reassignTarget.id}`, {
                instructor_id: reassignTo,
            });
            toast.success(
                `"${reassignTarget.title}" reassigned to ${
                    instructorById[reassignTo]?.name || "selected instructor"
                }.`,
            );
            setReassignTarget(null);
            setReassignTo("");
            await load();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not reassign. Try again.",
            );
        } finally {
            setReassigning(false);
        }
    };

    return (
        <motion.div
            variants={CONTAINER}
            initial="hidden"
            animate="show"
            className="max-w-7xl mx-auto px-6 lg:px-8 py-10 lg:py-14 space-y-8"
            data-testid="sme-dashboard"
        >
            {/* Hero */}
            <motion.section
                variants={ITEM}
                className="relative overflow-hidden rounded-3xl border border-primary/15 bg-gradient-to-br from-card via-card to-primary/[0.04] px-8 py-10"
            >
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-accent font-semibold">
                            <Radio className="h-3.5 w-3.5" />
                            Subject-matter expert
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl text-primary tracking-tight mt-3 leading-[1.05]">
                            Welcome, <span className="text-accent">{user?.name?.split(" ")[0] || ""}</span>.
                        </h1>
                        <p className="mt-3 text-base text-muted-foreground max-w-xl">
                            Author new programs and hand them off to the instructor
                            who will teach them. Edits stay open as long as you
                            need.
                        </p>
                    </div>
                    <Button
                        onClick={() => setCreateOpen(true)}
                        className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2"
                        data-testid="sme-new-program-btn"
                    >
                        <Plus className="h-4 w-4" />
                        New program
                    </Button>
                </div>
            </motion.section>

            {/* Background video-render progress (only renders when the
                current user has in-flight or just-finished jobs). */}
            <motion.div variants={ITEM}>
                <VideoRenderJobsStrip />
            </motion.div>

            {/* Stats */}
            <motion.div variants={ITEM} className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Stat
                    label="Programs in catalogue"
                    value={programs.filter((p) => !p.is_archived).length}
                    Icon={BookOpen}
                    testid="sme-stat-programs"
                />
                <Stat
                    label="Instructors available"
                    value={instructors.filter((i) => i.is_active).length}
                    Icon={Users}
                    testid="sme-stat-instructors"
                />
                <Stat
                    label="Unassigned"
                    value={programs.filter((p) => !p.is_archived && !p.instructor_id).length}
                    Icon={UserCog}
                    testid="sme-stat-unassigned"
                    tone="accent"
                />
            </motion.div>

            {/* My drafts — pinned strip for the SME's own unpublished work */}
            {myDrafts.length > 0 ? (
                <motion.section variants={ITEM} data-testid="sme-my-drafts-section">
                    <div className="flex items-end justify-between mb-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-accent font-semibold">
                                Pinned
                            </div>
                            <h2 className="font-display text-2xl text-primary mt-1 flex items-center gap-2">
                                <FileEdit className="h-5 w-5 text-accent" />
                                My drafts ({myDrafts.length})
                            </h2>
                        </div>
                        <p className="text-xs text-muted-foreground max-w-xs text-right">
                            Programs you authored that aren't live yet. Finish editing,
                            then hit Publish.
                        </p>
                    </div>
                    <div className="space-y-2">
                        {myDrafts.map((p) => {
                            const owner = instructorById[p.instructor_id];
                            return (
                                <Card
                                    key={p.id}
                                    className="border-amber-200 bg-amber-50/40"
                                    data-testid={`sme-draft-row-${p.id}`}
                                >
                                    <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Badge
                                                    variant="outline"
                                                    className="text-[10px] border-amber-400 text-amber-700"
                                                >
                                                    Draft
                                                </Badge>
                                                <span className="font-display text-lg text-primary truncate">
                                                    {p.title}
                                                </span>
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                                {p.subject} · {p.level}
                                                {" · "}
                                                {owner
                                                    ? <>Will be taught by <span className="text-foreground">{owner.name}</span></>
                                                    : <span className="text-amber-700">No instructor assigned</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="gap-1.5"
                                                asChild
                                                data-testid={`sme-draft-edit-${p.id}`}
                                            >
                                                <Link to={`/instructor/programs/${p.id}`}>
                                                    <Settings2 className="h-3.5 w-3.5" />
                                                    Continue editing
                                                </Link>
                                            </Button>
                                            <Button
                                                size="sm"
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                                                onClick={() => publishProgram(p)}
                                                disabled={publishingId === p.id}
                                                data-testid={`sme-draft-publish-${p.id}`}
                                            >
                                                {publishingId === p.id ? (
                                                    <>
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                        Publishing…
                                                    </>
                                                ) : (
                                                    <>
                                                        <Rocket className="h-3.5 w-3.5" />
                                                        Publish now
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </motion.section>
            ) : null}

            {/* Programs list */}
            <motion.section variants={ITEM} className="space-y-3">
                <div className="flex items-end justify-between mb-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-accent font-semibold">
                            Catalogue
                        </div>
                        <h2 className="font-display text-2xl text-primary mt-1">
                            All programs
                        </h2>
                    </div>
                </div>
                {loading ? (
                    <Card className="border-dashed">
                        <CardContent className="p-8 text-center text-sm text-muted-foreground">
                            Loading…
                        </CardContent>
                    </Card>
                ) : programs.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="p-10 text-center">
                            <Sparkles className="h-10 w-10 text-accent/60 mx-auto mb-3" />
                            <div className="font-display text-xl text-primary mb-1">
                                No programs yet
                            </div>
                            <div className="text-sm text-muted-foreground">
                                Click <strong>New program</strong> above to author your first one.
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    programs.map((p) => {
                        const owner = instructorById[p.instructor_id];
                        return (
                            <Card
                                key={p.id}
                                className="border-primary/10"
                                data-testid={`sme-program-row-${p.id}`}
                            >
                                <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-display text-lg text-primary truncate">
                                                {p.title}
                                            </span>
                                            {p.is_archived ? (
                                                <Badge variant="outline" className="text-[10px]">
                                                    Archived
                                                </Badge>
                                            ) : null}
                                            {!p.is_published ? (
                                                <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">
                                                    Draft
                                                </Badge>
                                            ) : null}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                            {p.subject} · {p.level}
                                            {" · "}
                                            {owner
                                                ? <>Owner: <span className="text-foreground">{owner.name}</span></>
                                                : <span className="text-amber-600">Unassigned</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-1.5"
                                            onClick={() => {
                                                setReassignTarget({
                                                    id: p.id,
                                                    title: p.title,
                                                    instructor_id: p.instructor_id,
                                                });
                                                setReassignTo(p.instructor_id || "");
                                            }}
                                            data-testid={`sme-reassign-btn-${p.id}`}
                                        >
                                            <UserCog className="h-3.5 w-3.5" />
                                            Reassign
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="bg-primary hover:bg-primary/90 gap-1.5"
                                            asChild
                                            data-testid={`sme-edit-btn-${p.id}`}
                                        >
                                            <Link to={`/instructor/programs/${p.id}`}>
                                                <Settings2 className="h-3.5 w-3.5" />
                                                Open editor
                                            </Link>
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-1.5 text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                                            onClick={() => setDeleteTarget({ id: p.id, title: p.title })}
                                            data-testid={`sme-delete-btn-${p.id}`}
                                            title={`Delete "${p.title}"`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })
                )}
            </motion.section>

            {/* My deleted programs — collapsed by default, shows only when
                the current SME has soft-deleted something. Restore is
                self-service so they don't need to ping an admin. */}
            {myDeletions.length > 0 && (
                <motion.section variants={ITEM} data-testid="sme-my-deletions-strip">
                    <details className="rounded-lg border border-border bg-card overflow-hidden">
                        <summary
                            className="cursor-pointer px-4 py-3 text-sm font-medium flex items-center gap-2 hover:bg-secondary/30 transition-colors"
                            data-testid="sme-my-deletions-toggle"
                        >
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                            My deleted programs
                            <Badge variant="outline" className="ml-1">
                                {myDeletions.length}
                            </Badge>
                            <span className="ml-auto text-xs text-muted-foreground font-normal">
                                Restore your own deletions
                            </span>
                        </summary>
                        <ul className="divide-y divide-border border-t border-border bg-secondary/10">
                            {myDeletions.map((p) => (
                                <li
                                    key={p.id}
                                    className="px-4 py-3 flex items-center gap-3"
                                    data-testid={`sme-deletion-row-${p.id}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-foreground truncate">
                                            {p.title}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {p.subject} · {p.level}
                                            {p.deleted_at && (
                                                <>
                                                    {" · deleted "}
                                                    {new Date(p.deleted_at).toLocaleDateString()}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="gap-1.5"
                                        onClick={() => restoreProgram(p)}
                                        disabled={restoringId === p.id}
                                        data-testid={`sme-restore-btn-${p.id}`}
                                    >
                                        {restoringId === p.id ? (
                                            <>
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                Restoring…
                                            </>
                                        ) : (
                                            <>
                                                <RotateCcw className="h-3.5 w-3.5" />
                                                Restore
                                            </>
                                        )}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    </details>
                </motion.section>
            )}

            {/* Reassign dialog */}
            <Dialog
                open={!!reassignTarget}
                onOpenChange={(o) => !o && !reassigning && setReassignTarget(null)}
            >
                <DialogContent data-testid="sme-reassign-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2 text-primary">
                            <UserCog className="h-5 w-5" />
                            Reassign program
                        </DialogTitle>
                        <DialogDescription>
                            Move <strong>{reassignTarget?.title}</strong> to a different instructor.
                            Their dashboard will start showing this program immediately.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Label>Instructor</Label>
                        <Select value={reassignTo} onValueChange={setReassignTo}>
                            <SelectTrigger data-testid="sme-reassign-select">
                                <SelectValue placeholder="Pick an instructor…" />
                            </SelectTrigger>
                            <SelectContent>
                                {instructors
                                    .filter((i) => i.is_active)
                                    .map((i) => (
                                        <SelectItem
                                            key={i.id}
                                            value={i.id}
                                            data-testid={`sme-reassign-opt-${i.id}`}
                                        >
                                            {i.name} · {i.email}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => !reassigning && setReassignTarget(null)}
                            disabled={reassigning}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitReassign}
                            disabled={!reassignTo || reassigning ||
                                reassignTo === reassignTarget?.instructor_id}
                            className="bg-primary hover:bg-primary/90 gap-2"
                            data-testid="sme-reassign-confirm-btn"
                        >
                            {reassigning ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                <>
                                    <Award className="h-4 w-4" />
                                    Save assignment
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CreateProgramWizard
                open={createOpen}
                onOpenChange={setCreateOpen}
                instructors={instructors}
                apiPath="/admin/programs"
                onCreated={load}
            />

            {/* Delete confirmation. Soft-deletes (admin can restore from trash). */}
            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(o) => {
                    if (!o && !deleting) setDeleteTarget(null);
                }}
            >
                <AlertDialogContent data-testid="sme-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-destructive" />
                            Delete this program?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <strong>{deleteTarget?.title}</strong> will be moved
                            to the admin trash. It'll be hidden from the public
                            catalogue and students immediately. An admin can
                            restore it from <code>/admin/programs</code> if you
                            change your mind.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>
                            Cancel
                        </AlertDialogCancel>
                        <Button
                            variant="destructive"
                            onClick={deleteProgram}
                            disabled={deleting}
                            data-testid="sme-delete-confirm-btn"
                            className="gap-2"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Deleting…
                                </>
                            ) : (
                                <>
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                </>
                            )}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </motion.div>
    );
}

function Stat({ label, value, Icon, testid, tone = "primary" }) {
    const ring = tone === "accent" ? "bg-accent/10 ring-accent/30" : "bg-primary/5 ring-primary/15";
    return (
        <Card data-testid={testid} className="border-primary/10">
            <CardContent className="p-5 flex items-start justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                        {label}
                    </div>
                    <div className="font-display text-3xl text-primary mt-2 tabular-nums">
                        {value}
                    </div>
                </div>
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ring-1 ${ring}`}>
                    <Icon className="h-4 w-4 text-primary" />
                </div>
            </CardContent>
        </Card>
    );
}
